import { lazy, Suspense, useEffect, useState } from "react";
import { Bus } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { addDays, format } from "date-fns";
import { api } from "./api";
import { activeSeasonOf, clampDate, jstToday, seasonsQuery } from "./seasons";
const InputPage = lazy(() => import("./pages/InputPage"));
const HistoryPage = lazy(() => import("./pages/HistoryPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));

type Tab = "input" | "history" | "analytics" | "settings";
const nav = [
  { id: "input" as const, label: "今日の入力", emoji: "📝" },
  { id: "history" as const, label: "履歴", emoji: "🕘" },
  { id: "analytics" as const, label: "分析・出力", emoji: "📊" },
  { id: "settings" as const, label: "設定", emoji: "⚙️" },
];

// 全アプリ共通の上のバー(入口で共通ログインしている人だけに出す。スクロールしても、ずっと表示)
const HUB = "https://basubasu.nobukofu.com/";
const hubNav = [
  { name: "ホーム", short: "ホーム", href: HUB, icon: "🏠", on: false },
  { name: "バスナビ管理", short: "バスナビ", href: "https://busnavi.nobukofu.com/admin", icon: "🚌", on: false },
  { name: "輸送人員", short: "輸送人員", href: "https://bus.nobukofu.com/", icon: "👥", on: true },
  { name: "シフト管理", short: "シフト", href: "https://shift.nobukofu.com/", icon: "📅", on: false },
];
function GlobalBar() {
  // いま開いているアプリのタブが、横スクロールで見えない位置にあるときは、見える位置へ動かす
  useEffect(() => { document.querySelector(".gbar [aria-current]")?.scrollIntoView({ inline: "center", block: "nearest" }); }, []);
  return <header className="gbar"><div className="gbar-in">
    <a className="gbar-brand" href={HUB}>バス関連アプリ</a>
    <nav aria-label="バス関連アプリ">{hubNav.map((n) => <a key={n.name} className={`pill${n.on ? " active" : ""}`} href={n.href} aria-current={n.on ? "page" : undefined}><span className="ic" aria-hidden="true">{n.icon}</span><span className="lg">{n.name}</span><span className="sm">{n.short}</span></a>)}</nav>
    <a className="gbar-logout" href={`${HUB}logout`}>ログアウト</a>
  </div></header>;
}

export default function App() {
  const queryClient = useQueryClient();
  const session = useQuery({ queryKey: ["session"], queryFn: () => api<{ authenticated: boolean; hub?: boolean }>("/api/auth/session"), retry: false });
  const [tab, setTab] = useState<Tab>("input");
  const [inputDate, setInputDate] = useState<string | null>(null);
  const lastEntryQuery = useQuery({
    queryKey: ["last-input-day"],
    queryFn: () => api<{ rows: Array<{ operationDate: string; recordCount: number }> }>("/api/history"),
    enabled: session.data?.authenticated === true,
    retry: false,
  });
  const seasonsList = useQuery({ ...seasonsQuery, enabled: session.data?.authenticated === true, retry: false });
  useEffect(() => {
    if (inputDate != null) return;
    if (lastEntryQuery.isError || seasonsList.isError) { setInputDate(jstToday()); return; }
    if (!lastEntryQuery.isSuccess || !seasonsList.isSuccess) return;
    const lastEntered = lastEntryQuery.data.rows.find((row) => row.recordCount > 0);
    const base = lastEntered ? addDays(new Date(`${lastEntered.operationDate}T00:00:00`), 1) : new Date(`${jstToday()}T00:00:00`);
    const season = activeSeasonOf(seasonsList.data.rows);
    const next = format(base, "yyyy-MM-dd");
    setInputDate(season ? clampDate(next, season) : next);
  }, [inputDate, lastEntryQuery.isSuccess, lastEntryQuery.isError, lastEntryQuery.data, seasonsList.isSuccess, seasonsList.isError, seasonsList.data]);
  if (session.isLoading) return <div className="splash"><Bus size={42}/><p>読込中…</p></div>;
  if (session.isError) return <Login onSuccess={() => void queryClient.invalidateQueries({ queryKey: ["session"] })}/>;
  if (inputDate == null) return <div className="splash"><Bus size={42}/><p>読込中…</p></div>;
  const hub = session.data?.hub === true;
  return <div className={`app-shell${hub ? " has-gbar" : ""}`}>
    {hub && <GlobalBar/>}
    <header className="topbar"><div className="topbar-in">
      <button className="brand-text pill" style={{ padding: 0 }} onClick={() => setTab("input")}>輸送人員管理</button>
      <nav className="pills">
        {nav.map(({ id, label, emoji }) => <button key={id} className={`pill${tab === id ? " active" : ""}`} onClick={() => setTab(id)}><span aria-hidden="true">{emoji}</span>{label}</button>)}
      </nav>
      {!hub && <button className="logout-text" onClick={async () => { await api("/api/auth/logout", { method: "POST" }); queryClient.clear(); setInputDate(null); void session.refetch(); }}>ログアウト</button>}
    </div></header>
    <main className="content"><Suspense fallback={<div className="page-loading"><span/></div>}>{tab === "input" ? <InputPage initialDate={inputDate} onDateChange={setInputDate}/> : tab === "history" ? <HistoryPage onOpenDay={(date) => { setInputDate(date); setTab("input"); }}/> : tab === "analytics" ? <AnalyticsPage/> : <SettingsPage/>}</Suspense></main>
  </div>;
}

function Login({ onSuccess }: { onSuccess: () => void }) {
  const [password, setPassword] = useState(""); const [error, setError] = useState(""); const [busy, setBusy] = useState(false);
  useEffect(() => { document.title = "シャトルバス輸送人員管理"; }, []);
  return <main className="login-page"><section className="login-card"><div className="login-icon"><Bus size={34}/></div>
    <h1>シャトルバス<br/>輸送人員管理</h1><p>管理用パスワードを入力してください</p>
    <form onSubmit={async (event) => { event.preventDefault(); setBusy(true); setError(""); try { await api("/api/auth/login", { method: "POST", body: JSON.stringify({ password }) }); onSuccess(); } catch (e) { setError(e instanceof Error ? e.message : "ログインできませんでした"); } finally { setBusy(false); } }}>
      <label>パスワード<input autoFocus type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"/></label>
      {error && <div className="error-banner">{error}</div>}<button className="primary wide" disabled={busy}>{busy ? "確認中…" : "ログイン"}</button>
    </form><small className="login-note">30日間ログイン状態を保持します</small>
  </section></main>;
}
